<template>
    <div id="task-list-view">
        <div v-if="isLoading && tasks.length === 0" class="loading-container">
            <a-spin size="large">
                <span slot="tip" class="loading-text">Görevler yükleniyor...</span>
            </a-spin>
        </div>
        <div v-else class="kanban-task-table">
            <supsis-table
                :columns="columns"
                :rows="tasks"
                :bottom-pagination="true"
                :pagination-cursor="paginationCursor"
                :options="tableOptions"
                :loading="isLoading"
                @cursor-changed="handlePaginationChange"
            />
        </div>
    </div>
</template>
<script>
import { mapActions, mapState } from "vuex";
import Avatar from "vue-avatar";
import { getOrUpdateFilters } from "../../utils/persistenceStorage";
import SupsisTable from "../SupsisTable.vue";
import LowIcon from "../svg/task/LowIcon.vue";
import LowestIcon from "../svg/task/LowestIcon.vue";
import MediumIcon from "../svg/task/MediumIcon.vue";
import HighIcon from "../svg/task/HighIcon.vue";
import HighestIcon from "../svg/task/HighestIcon.vue";
import SupsisButton from "../../views/Pages/Settings/components/SupsisButton.vue";
import { deleteTask } from "../../api/tasks";
import { publishMessage, SystemMessageTypes } from "../../utils/systemPubSub";

const filters = getOrUpdateFilters("kanbanlist");
export default {
    name: "TaskListView",
    components: {
        SupsisTable,
        Avatar,
        LowIcon,
        LowestIcon,
        MediumIcon,
        HighIcon,
        HighestIcon,
        SupsisButton,
    },
    props: {
        visitorId: {
            type: String,
            default: null,
        },
        tableRecord: {
            type: String,
            default: null,
        },
        assignees: {
            type: Array,
            default: () => [],
        },
        owners: {
            type: Array,
            default: () => [],
        },
        followers: {
            type: Array,
            default: () => [],
        },
        workflowId: {
            type: String,
            default: null,
        },
        pickerMode: {
            type: Boolean,
            default: false,
        },
        showOverdue: {
            type: Boolean,
            default: false,
        },
        search: {
            type: String,
            default: '',
        },
        selectedPriorities: {
            type: Array,
            default: () => [],
        },
        startDateRange: {
            type: Array,
            default: () => null,
        },
        endDateRange: {
            type: Array,
            default: () => null,
        },
        dateRange: {
            type: Array,
            default: () => null,
        },
        createdBy: {
            type: Array,
            default: () => [],
        },
        selectedTags: {
            type: Array,
            default: () => [],
        },
        updateAtRange: {
            type: Array,
            default: () => null,
        },
        selectedPipelines: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            isLoading: false,
            tasks: [],
            lastPage: 1,
            isSearching: false,
            paginationCursor: {
                total: 0,
                page: filters.page || 1,
                perPage: filters.perPage || 20,
            },
            isUpdatingFromParent: false,
            columns: [
                ...(this.pickerMode
                    ? [
                        {
                            title: "",
                            field: "select",
                            key: "select",
                            width: 80,
                            renderBodyCell: ({ row }) => {
                                return (
                                    <SupsisButton xxsmall onClick={() => this.$emit("selected", row)}>
                                        Seç
                                    </SupsisButton>
                                );
                            },
                        },
                    ]
                    : []),
                {
                    title: this.$t("task.taskID"),
                    field: "customId",
                    key: "customId",
                    renderBodyCell: ({ row }) => {
                        return (
                            <div class="task-id-cell">
                                <span class="material-icons">fingerprint</span>
                                <span class="task-id-text">{row.customId}</span>
                            </div>
                        );
                    },
                },
                {
                    title: this.$t("message.lastUpdate"),
                    field: "updateDate",
                    key: "updateDate",
                    renderBodyCell: ({ row }) => {
                        return row?.dates?.updateDate ? (
                            <div class="date-cell">
                                <span class="material-icons">schedule</span>
                                <span>{this.formatDate(row?.dates?.updateDate)}</span>
                            </div>
                        ) : "";
                    },
                },
                {
                    title: this.$t("message.workflow"),
                    field: "workflow",
                    key: "workflow",
                    renderBodyCell: ({ row }) => {
                        const workflow = row?.workflow && this.workflows[row?.workflow?._id];
                        return workflow?.title ? (
                            <div class="workflow-cell">
                                <span class="material-icons">view_kanban</span>
                                <span>{workflow.title}</span>
                            </div>
                        ) : "";
                    },
                },
                {
                    title: this.$t("message.task"),
                    field: "title",
                    key: "title",
                    renderBodyCell: ({ row }) => {
                        return (
                            <div class={"title-cell"} v-tippy={{ content: row.title }}>
                                {row.title}
                            </div>
                        );
                    },
                },
                {
                    title: this.$t("task.stage"),
                    field: "pipelineStage",
                    key: "pipelineStage",
                    renderBodyCell: ({ row }) => {
                        return row?.pipelineStage ? (
                            <div
                                class="supsis-tag-el"
                                style={{ "--pipeline-color": row?.pipelineStage?.color || "#000" }}
                            >
                                {row?.pipelineStage?.name || ""}
                            </div>
                        ) : null;
                    },
                },
                {
                    title: this.$t("task.owner"),
                    field: "owner",
                    key: "owner",
                    renderBodyCell: ({ row }) => {
                        const user = row.owner ? this.users[row.owner] : null;
                        return user && (
                            <div class="agent-detail">
                                <Avatar
                                    size={25}
                                    key={user._id}
                                    username={user.name}
                                    src={user.gravatar}
                                    v-tippy={{
                                        followCursor: true,
                                        placement: "bottom-end",
                                    }}
                                    content={user.email}
                                />
                                <div v-tippy={{ content: user.name }}>
                                    {user.name.substring(0, 8) + "..."}
                                </div>
                            </div>
                        );
                    },
                },
                {
                    title: this.$t("task.assignees"),
                    field: "assignees",
                    key: "assignees",
                    renderBodyCell: ({ row }) => {
                        const assignee = row?.assignees?.[0];
                        const user = assignee ? this.users[assignee] : null;
                        return user && (
                            <div class="agent-detail">
                                <Avatar
                                    size={25}
                                    key={assignee}
                                    src={user?.gravatar}
                                    username={user?.name}
                                    v-tippy={{
                                        followCursor: true,
                                        placement: "bottom-end",
                                    }}
                                    content={user?.email}
                                />
                                <div v-tippy={{ content: user?.name }}>
                                    {(user?.name || "").substring(0, 8) + "..."}
                                </div>
                            </div>
                        );
                    },
                },
                {
                    title: this.$t("message.taskCreator"),
                    field: "createdBy",
                    key: "createdBy",
                    renderBodyCell: ({ row }) => {
                        const userId = row?.createdBy?.user;
                        const user = userId ? this.users[userId] : null;
                        return user && (
                            <div class="agent-detail">
                                <Avatar
                                    size={25}
                                    key={userId}
                                    src={user?.gravatar}
                                    username={user?.name}
                                    v-tippy={{
                                        followCursor: true,
                                        placement: "bottom-end",
                                    }}
                                    content={user?.email}
                                />
                                <div v-tippy={{ content: user?.name }}>
                                    {(user?.name || "").substring(0, 8) + "..."}
                                </div>
                            </div>
                        );
                    },
                },
                {
                    title: this.$t("message.createdDate"),
                    field: "createdAt",
                    key: "createdAt",
                    renderBodyCell: ({ row }) => {
                        return (
                            <div class="date-cell">
                                <span class="material-icons">event</span>
                                <span>{this.formatDate(row.createdAt)}</span>
                            </div>
                        );
                    },
                },
                {
                    title: this.$t("message.startDate"),
                    field: "startDate",
                    key: "startDate",
                    renderBodyCell: ({ row }) => {
                        return row.startDate ? (
                            <div class="date-cell">
                                <span class="material-icons">play_circle</span>
                                <span>{this.formatDate(row.startDate)}</span>
                            </div>
                        ) : "";
                    },
                },
                {
                    title: this.$t("message.priority"),
                    field: "priority",
                    key: "priority",
                    renderBodyCell: ({ row }) => {
                        return row.priority === "low" ? (
                            <div v-tippy={{ content: "Low" }}>
                                <LowIcon />
                            </div>
                        ) : row.priority === "lowest" ? (
                            <div v-tippy={{ content: "Lowest" }}>
                                <LowestIcon />
                            </div>
                        ) : row.priority === "medium" ? (
                            <div v-tippy={{ content: "Medium" }}>
                                <MediumIcon />
                            </div>
                        ) : row.priority === "high" ? (
                            <div v-tippy={{ content: "High" }}>
                                <HighIcon />
                            </div>
                        ) : row.priority === "highest" ? (
                            <div v-tippy={{ content: "Highest" }}>
                                <HighestIcon />
                            </div>
                        ) : (
                            ""
                        );
                    },
                },
                {
                    title: this.$t("message.endDate"),
                    field: "endDate",
                    key: "endDate",
                    renderBodyCell: ({ row }) => {
                        if (!row.endDate) return "";
                        const now = new Date();
                        const endDate = new Date(row.endDate);
                        const isOverdue = endDate < now;
                        return (
                            <div class="date-cell" style={{ color: isOverdue ? '#f5576c' : '#6c757d' }}>
                                <span class="material-icons" style={{ color: isOverdue ? '#f5576c' : '#91a2b6' }}>
                                    {isOverdue ? 'warning' : 'flag'}
                                </span>
                                <span>{this.formatDate(row.endDate)}</span>
                            </div>
                        );
                    },
                },
                {
                    title: this.$t("message.transactions"),
                    field: "_id",
                    key: "_id",
                    copyDisabled: true,
                    renderBodyCell: ({ row }) => {
                        const refreshTable = this.refreshTable;
                        return (
                            <div class="task-transactions">
                                <SupsisButton
                                    xxsmall
                                    icon="edit"
                                    onClick={() => {
                                        this.addNewWindow({
                                            type: "editor-task",
                                            title: row.title,
                                            payload: {
                                                taskId: row._id,
                                                onTaskCreated: () => {
                                                    refreshTable();
                                                },
                                            },
                                        });
                                    }}
                                ></SupsisButton>
                                <SupsisButton
                                    xxsmall
                                    icon="delete"
                                    danger
                                    onClick={() => this.confirmDeleteTask(row)}
                                ></SupsisButton>
                            </div>
                        );
                    },
                },
            ],
            tableOptions: {
                bodyCellEvents: ({ row, column, rowIndx }) => {
                    return {
                        dblclick: (event) => {
                            this.addNewWindow({
                                type: "edit-workflow",
                                title: row.title,
                                payload: {
                                    taskId: row._id,
                                },
                            });
                        },
                    };
                },
            },
        };
    },
    computed: {
        ...mapState({
            user: (state) => state.auth.user,
            users: (state) =>
                state.user.users.data.reduce((acc, user) => {
                    acc[user._id] = user;
                    return acc;
                }, {}),
            workflows: (state) =>
                state.tasks.workflows.reduce((acc, workflow) => {
                    acc[workflow._id] = workflow;
                    return acc;
                }, {}),
        }),
    },
    mounted() {
        this.loadData();
    },
    methods: {
        ...mapActions({
            addNewWindow: "app/addNewWindow",
            getListTasks: "tasks/getListTasks",
        }),
        loadData() {
            console.log("Veri yükleniyor...");

            this.isUpdatingFromParent = true;

            this.getTasks();

            this.$nextTick(() => {
                this.isUpdatingFromParent = false;
            });
        },
        handlePaginationChange(cursor) {
            console.log("Sayfalama değişti:", cursor);
            this.paginationCursor = {
                ...this.paginationCursor,
                page: cursor.page,
                perPage: cursor.perPage
            };
            if (!this.isSearching) {
                this.lastPage = cursor.page;
            }
            this.getTasks();
        },
        resetPagination() {
            if ((this.search && this.search.length > 0) ||
                (this.selectedPriorities && this.selectedPriorities.length > 0) ||
                (this.selectedPipelines && this.selectedPipelines.length > 0) ||
                (this.owners && this.owners.length > 0) ||
                (this.followers && this.followers.length > 0) ||
                (this.createdBy && this.createdBy.length > 0) ||
                (this.selectedTags && this.selectedTags.length > 0) ||
                this.startDateRange ||
                this.endDateRange ||
                this.updateAtRange ||
                this.dateRange) {
                this.isSearching = true;
                this.paginationCursor = {
                    ...this.paginationCursor,
                    page: 1
                };
            }
        },
        refreshTable() {
            this.resetPagination();
            this.getTasks();
        },
        formatDate(date) {
            return date && this.$moment(date).format("YYYY/MM/DD HH:mm");
        },
        getTasks() {
            if (this.isLoading) return;

            this.isLoading = true;
            getOrUpdateFilters("kanbanlist", {
                assignees: this.assignees,
                owners: this.owners,
                followers: this.followers,
                showOverdue: this.showOverdue,
                page: this.paginationCursor.page,
                perPage: this.paginationCursor.perPage,
                search: this.search,
                selectedPriorities: this.selectedPriorities,
                selectedPipelines: this.selectedPipelines,
                selectedTags: this.selectedTags,
                dateRange: this.dateRange,
                startDateRange: this.startDateRange,
                endDateRange: this.endDateRange,
                updateAtRange: this.updateAtRange,
                createdBy: this.createdBy
            });

            const params = {
                assignees: this.assignees,
                page: this.paginationCursor.page,
                perPage: this.paginationCursor.perPage,
                visitorId: this.visitorId,
                tableRecord: this.tableRecord,
                showOverdue: this.showOverdue,
                search: this.search,
                priorities: this.selectedPriorities,
                pipelines: this.selectedPipelines,
                owner: this.owners,
                followers: this.followers,
                dateRange: this.dateRange,
                createdBy: this.createdBy,
                tags: this.selectedTags,
                startDateRange: this.startDateRange,
                endDateRange: this.endDateRange,
                updateAtRange: this.updateAtRange,
                workflowId: this.workflowId
            };

            console.log('Liste Görünümü - Filtrelerle görevler yükleniyor:', params);

            this.getListTasks(params).then(({ data, total }) => {
                this.tasks = data;
                this.paginationCursor = {
                    ...this.paginationCursor,
                    total
                };
            }).catch(error => {
                console.error('Görevler yüklenirken hata oluştu:', error);
                this.$notification.error({
                    message: 'Hata',
                    description: 'Görevler yüklenirken bir hata oluştu.'
                });
            }).finally(() => {
                this.isLoading = false;
            });
        },
        confirmDeleteTask(task) {
            const workflow = task.workflow;
            if (workflow && workflow.permissions) {
                const permissions = ["createTask", "editTask", "draggingTask", "deleteTask"].map(
                    (permission) => workflow.permissions[permission] || [],
                );
                const perm = permissions.every((permission) => permission.length === 0);
                if (perm || permissions[3].includes(this.user._id)) {
                    this.$confirm({
                        title: "Silme İşlemi",
                        content: "Bu görevi silmek istediğinize emin misiniz?",
                        okText: this.$t("message.yes"),
                        cancelText: this.$t("message.no"),
                        onOk: () => {
                            this.deleteTask(task._id);
                        },
                    });
                } else {
                    this.$notification.error({
                        message: this.$t("task.deleteTaskError"),
                        description: this.$t("task.deleteTaskErrorDesc"),
                    });
                }
            } else {
                console.error("Workflow permissions are undefined");
            }
        },
        deleteTask(taskId) {
            deleteTask(taskId)
                .then(() => {
                    publishMessage(SystemMessageTypes.TASK_DELETED, {
                        taskId,
                    });
                    this.getTasks();
                    this.$message.success(`Görev başarıyla silindi`);
                })
                .catch((error) => {
                    console.error("Görev silinirken hata oluştu:", error);
                    this.$message.error(`Görev silinirken hata oluştu`);
                });
        },
        applyFilters(filters) {
            console.log('TaskListView - Filtreler uygulanıyor:', filters);

            this.isUpdatingFromParent = true;

            if (filters.assignees !== undefined) this.$emit('update:assignees', filters.assignees);
            if (filters.owners !== undefined) this.$emit('update:owners', filters.owners);
            if (filters.followers !== undefined) this.$emit('update:followers', filters.followers);
            if (filters.createdBy !== undefined) this.$emit('update:createdBy', filters.createdBy);
            if (filters.showOverdue !== undefined) this.$emit('update:showOverdue', filters.showOverdue);
            if (filters.selectedPriorities !== undefined) this.$emit('update:selectedPriorities', filters.selectedPriorities);
            if (filters.selectedTags !== undefined) this.$emit('update:selectedTags', filters.selectedTags);
            if (filters.selectedPipelines !== undefined) this.$emit('update:selectedPipelines', filters.selectedPipelines);
            if (filters.dateRange !== undefined) this.$emit('update:dateRange', filters.dateRange);
            if (filters.startDateRange !== undefined) this.$emit('update:startDateRange', filters.startDateRange);
            if (filters.endDateRange !== undefined) this.$emit('update:endDateRange', filters.endDateRange);
            if (filters.updateAtRange !== undefined) this.$emit('update:updateAtRange', filters.updateAtRange);
            if (filters.search !== undefined) this.$emit('update:search', filters.search);

            this.$nextTick(() => {
                this.resetPagination();
                this.getTasks();

                this.$nextTick(() => {
                    this.isUpdatingFromParent = false;
                });
            });
        },
        handlePropChanges() {
            if (this.isUpdatingFromParent) {
                this.resetPagination();
                this.getTasks();
            }
        }
    },
    watch: {
        search: {
            handler(newVal) {
                this.$emit('update:search', newVal);
            }
        },
        assignees: {
            handler(newVal) {
                this.$emit('update:assignees', newVal);

                if (this.isUpdatingFromParent) {
                    this.lastPage = 1;
                    this.resetPagination();
                    this.getTasks();
                }
            },
            deep: true
        },
        showOverdue: {
            handler(newVal) {
                this.$emit('update:showOverdue', newVal);

                if (this.isUpdatingFromParent) {
                    this.lastPage = 1;
                    this.resetPagination();
                    this.getTasks();
                }
            }
        },
        selectedPriorities: {
            handler(newVal) {
                this.$emit('update:selectedPriorities', newVal);

                if (this.isUpdatingFromParent) {
                    this.lastPage = 1;
                    this.resetPagination();
                    this.getTasks();
                }
            },
            deep: true
        },
        startDateRange: {
            handler(newVal) {
                this.$emit('update:startDateRange', newVal);

                if (this.isUpdatingFromParent) {
                    this.lastPage = 1;
                    this.resetPagination();
                    this.getTasks();
                }
            }
        },
        endDateRange: {
            handler(newVal) {
                this.$emit('update:endDateRange', newVal);

                if (this.isUpdatingFromParent) {
                    this.lastPage = 1;
                    this.resetPagination();
                    this.getTasks();
                }
            }
        },
        updateAtRange: {
            handler(newVal) {
                this.$emit('update:updateAtRange', newVal);

                if (this.isUpdatingFromParent) {
                    this.lastPage = 1;
                    this.resetPagination();
                    this.getTasks();
                }
            }
        },
        dateRange: {
            handler(newVal) {
                this.$emit('update:dateRange', newVal);

                if (this.isUpdatingFromParent) {
                    this.lastPage = 1;
                    this.resetPagination();
                    this.getTasks();
                }
            }
        },
        owners: {
            handler(newVal) {
                this.$emit('update:owners', newVal);

                if (this.isUpdatingFromParent) {
                    this.lastPage = 1;
                    this.resetPagination();
                    this.getTasks();
                }
            },
            deep: true
        },
        followers: {
            handler(newVal) {
                this.$emit('update:followers', newVal);

                if (this.isUpdatingFromParent) {
                    this.lastPage = 1;
                    this.resetPagination();
                    this.getTasks();
                }
            },
            deep: true
        },
        createdBy: {
            handler(newVal) {
                this.$emit('update:createdBy', newVal);

                if (this.isUpdatingFromParent) {
                    this.lastPage = 1;
                    this.resetPagination();
                    this.getTasks();
                }
            },
            deep: true
        },
        selectedTags: {
            handler(newVal) {
                this.$emit('update:selectedTags', newVal);

                if (this.isUpdatingFromParent) {
                    this.lastPage = 1;
                    this.resetPagination();
                    this.getTasks();
                }
            },
            deep: true
        },
        workflowId: {
            handler() {
                this.lastPage = 1;
                this.resetPagination();
                this.getTasks();
            }
        },
        selectedPipelines: {
            handler(newVal) {
                this.$emit('update:selectedPipelines', newVal);

                if (this.isUpdatingFromParent) {
                    this.lastPage = 1;
                    this.resetPagination();
                    this.getTasks();
                }
            },
            deep: true
        }
    }
};
</script>
<style lang="scss">
#task-list-view {
    background: #fafbfc;
    min-height: 100%;
    padding: 20px;

    .loading-container {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 400px;
        background: white;
        border-radius: 8px;
        border: 1px solid #e8ebed;

        .loading-text {
            color: #6c757d;
            font-size: 14px;
            margin-top: 12px;
        }
    }

    .task-transactions {
        display: flex;
        gap: 6px;
        align-items: center;
        justify-content: center;

        .supsis-button {
            padding: 6px 10px !important;
            height: 30px !important;
            border-radius: 4px !important;

            .material-icons {
                font-size: 16px;
            }
        }
    }

    .kanban-task-search {
        display: flex;
        justify-content: center;
        margin-top: 12px;
    }

    .title-cell {
        max-width: 300px;
        -webkit-line-clamp: 1;
        line-clamp: 1;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
        font-weight: 500;
        color: #2c3e50;
        font-size: 14px;
    }

    .kanban-task-table {
        padding: 0;
        background: white;
        border-radius: 8px;
        border: 1px solid #e8ebed;
        overflow: hidden;

        :deep(.supsis-table) {
            border-radius: 8px;
            
            .table-header {
                background: #f8f9fa;
                border-bottom: 2px solid #e8ebed;
                
                th {
                    color: #495057 !important;
                    font-weight: 600;
                    font-size: 13px;
                    padding: 14px 12px !important;
                    border-bottom: none !important;
                }
            }

            .table-body {
                tr {
                    border-bottom: 1px solid #f0f2f5;

                    &:hover {
                        background: #f8f9fa !important;
                    }

                    &:last-child {
                        border-bottom: none;
                    }

                    td {
                        padding: 12px !important;
                        vertical-align: middle;
                        color: #55555a;
                        font-size: 13px;
                    }
                }
            }
        }
    }

    .supsis-tag-el {
        padding: 5px 12px;
        border: 1px solid var(--pipeline-color);
        color: var(--pipeline-color);
        border-radius: 4px;
        width: max-content;
        font-weight: 500;
        font-size: 12px;
        background: transparent;
    }

    .agent-detail {
        display: flex;
        align-items: center;
        gap: 10px;
        
        > div:last-child {
            font-size: 13px;
            font-weight: 400;
            color: #495057;
            max-width: 120px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }

    .paginator {
        padding: 16px;
        display: flex;
        align-items: center;
        justify-content: end;
        background: white;
        border-radius: 0 0 8px 8px;
    }

    .task-id-cell {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 5px;
        padding: 4px 10px;
        background: #495057;
        border-radius: 4px;
        width: max-content;
        margin: 0 auto;

        .material-icons {
            color: white;
            font-size: 14px;
        }

        .task-id-text {
            color: white;
            font-weight: 600;
            font-size: 12px;
        }
    }

    .date-cell {
        display: flex;
        align-items: center;
        gap: 6px;
        color: #6c757d;
        font-size: 13px;

        .material-icons {
            font-size: 16px;
            color: #adb5bd;
        }
    }

    .workflow-cell {
        display: flex;
        align-items: center;
        gap: 6px;
        color: #495057;
        font-weight: 500;

        .material-icons {
            font-size: 16px;
            color: #6c757d;
        }
    }
}
</style>
